import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, AlertCircle, Loader2, CheckCircle, Lock } from 'lucide-react';
import { signIn, signInWithProvider } from '../lib/auth';
import { useAuth } from '../components/AuthProvider';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Retour à la page demandée après login (deep-link /app/...), sinon accueil V2.
  const redirectTo = (location.state as any)?.from?.pathname || '/app';
  const { isAuthenticated, isLoading: isAuthLoading, refreshAuth } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !isAuthLoading) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, isAuthLoading, navigate, redirectTo]);

  useEffect(() => {
    const remembered = localStorage.getItem('hooklab-remembered-email');
    if (remembered) { setEmail(remembered); setRememberMe(true); }
  }, []);

  const validateField = (field: string, value: string) => {
    const errors = { ...fieldErrors };
    if (field === 'email') {
      if (!value.trim()) errors.email = 'Email requis';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errors.email = 'Email invalide';
      else delete errors.email;
    }
    if (field === 'password') {
      if (!value) errors.password = 'Mot de passe requis';
      else delete errors.password;
    }
    setFieldErrors(errors);
  };

  const onEmail = (e: React.ChangeEvent<HTMLInputElement>) => { setEmail(e.target.value); validateField('email', e.target.value); setError(null); };
  const onPassword = (e: React.ChangeEvent<HTMLInputElement>) => { setPassword(e.target.value); validateField('password', e.target.value); setError(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    validateField('email', email);
    validateField('password', password);
    if (!email || !password) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await signIn(email, password);
      await refreshAuth();
      if (rememberMe) localStorage.setItem('hooklab-remembered-email', email);
      else localStorage.removeItem('hooklab-remembered-email');
      setIsSuccess(true);
      setTimeout(() => navigate(redirectTo, { replace: true }), 800);
    } catch (err: any) {
      if (err.message?.includes('Invalid login credentials')) setError('Email ou mot de passe incorrect.');
      else if (err.message?.includes('Email not confirmed')) setError("Votre email n'a pas encore été confirmé.");
      else setError(err.message || 'Erreur de connexion');
    } finally {
      setIsSubmitting(false);
    }
  };

  // SSO Google / Microsoft. Redirige vers le provider (ou message clair si pas encore
  // configuré côté Supabase).
  const onSso = async (provider: 'google' | 'azure') => {
    setError(null);
    try {
      await signInWithProvider(provider);
    } catch (err: any) {
      const msg = String(err?.message || '');
      if (msg.includes('not enabled') || msg.includes('provider') || msg.includes('Unsupported'))
        setError('Connexion SSO pas encore activée — configuration en cours.');
      else setError(msg || 'Erreur de connexion SSO');
    }
  };

  if (isAuthLoading) {
    return (
      <div className="refonte-scope min-h-screen grid place-items-center">
        <div className="flex items-center gap-2 text-dim font-mono text-[13px]">
          <Loader2 className="h-4 w-4 animate-spin text-hook-rose" /> Vérification de la session…
        </div>
      </div>
    );
  }

  const inputBase =
    'w-full rounded-lg border bg-panel px-3 py-2.5 text-[14px] text-ink placeholder:text-faint ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose transition';

  return (
    <div className="refonte-scope min-h-screen flex flex-col justify-center px-4 py-10">
      <div className="w-full max-w-[400px] mx-auto">
        {/* Logo + titre */}
        <div className="text-center mb-8">
          <img src="/hook-logo.png" alt="Hook" className="h-16 w-auto mx-auto" />
          <p className="mt-4 text-dim text-[14px]">Connectez-vous à votre espace.</p>
        </div>

        <div className="bg-panel border border-line rounded-card shadow-sm p-6 sm:p-7">
          {/* SSO — Microsoft masqué (Hook = Google Workspace, pas de tenant Azure) */}
          <div className="mb-5">
            <button type="button" onClick={() => onSso('google')}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-line-2 py-2.5 text-[13px] font-semibold hover:border-ink transition">
              <span aria-hidden>G</span> Continuer avec Google
            </button>
          </div>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px flex-1 bg-line" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-faint">ou par email</span>
            <span className="h-px flex-1 bg-line" />
          </div>

          {isSuccess && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-st-ok/10 text-st-ok px-3 py-2 text-[13px]">
              <CheckCircle className="h-4 w-4" /> Connexion réussie — redirection…
            </div>
          )}
          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-st-bad/10 text-st-bad px-3 py-2 text-[13px]">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-[12px] font-semibold text-dim mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-faint" aria-hidden />
                <input id="email" type="email" autoComplete="email" required value={email} onChange={onEmail}
                  disabled={isSubmitting || isSuccess}
                  className={`${inputBase} pl-9 ${fieldErrors.email ? 'border-st-bad' : 'border-line-2'}`}
                  placeholder="vous@entreprise.com" />
              </div>
              {fieldErrors.email && <p className="mt-1 text-[11px] text-st-bad">{fieldErrors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-[12px] font-semibold text-dim mb-1.5">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-faint" aria-hidden />
                <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required
                  value={password} onChange={onPassword} disabled={isSubmitting || isSuccess}
                  className={`${inputBase} pl-9 pr-9 ${fieldErrors.password ? 'border-st-bad' : 'border-line-2'}`}
                  placeholder="••••••••" />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-faint hover:text-ink">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {fieldErrors.password && <p className="mt-1 text-[11px] text-st-bad">{fieldErrors.password}</p>}
            </div>

            <div className="flex items-center justify-between text-[12.5px]">
              <label className="flex items-center gap-2 text-dim cursor-pointer">
                <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                  className="accent-hook-rose w-4 h-4" />
                Se souvenir de moi
              </label>
              <Link to="/login/code" state={{ email, reason: 'forgot' }} className="text-hook-rose font-semibold hover:underline">
                Mot de passe oublié ?
              </Link>
            </div>

            <button type="submit" disabled={isSubmitting || isSuccess}
              className="w-full rounded-lg bg-ink text-white py-2.5 text-[14px] font-semibold hover:opacity-90 disabled:opacity-50 transition flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose">
              {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Connexion…</> : 'Se connecter'}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t border-line text-center">
            <p className="text-[12.5px] text-dim mb-1.5">Première connexion, ou pas de mot de passe ?</p>
            <Link to="/login/code" state={{ email }} className="text-[13px] font-semibold text-hook-rose hover:underline">
              Recevoir un code par email →
            </Link>
          </div>
        </div>

        <p className="text-center mt-5 font-mono text-[10px] text-faint uppercase tracking-widest">
          HookLab · accès sur invitation
        </p>
      </div>

    </div>
  );
};

export default LoginPage;
